import React, { useState } from 'react';
import { useQuery } from 'react-query';

import styled from 'styled-components';

import { getAllCommunities } from 'apis/CommunityAPI';
import { CommunityType } from 'types/communities';
import useDataList from 'utils/hooks/useDataList';

import { CommunitiesContext } from './CommunitiesContext';
import CommunitiesControls from './CommunitiesControls';
import CommunitiesHeader from './CommunitiesHeader';
import { COMMUNITIES_ADMIN_PAGE_SIZE, ORDERING_FIELD } from './constants';
import ManageContactsModal from './contacts/ManageContactsModal';
import ManageIncentivesModal from './incentives/ManageIncentivesModal';
import CommunitiesTable from './table/CommunitiesTable';
import { COMPANY_COMMUNITY_RELATION_TYPE } from '../../constants/company';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding: 10px 30px;
`;

const Communities = () => {
  const [selectedCommunityInfo, setSelectedCommunityInfo] = useState<CommunityType | undefined>(undefined);
  const [manageContactsOpened, setManageContactsOpened] = useState<boolean>(false);
  const [manageIncentivesOpened, setManageIncentivesOpened] = useState<boolean>(false);

  const { items, ordering, totalItemsCount, filters, setItems, setOrdering, setPage, setFilters } = useDataList({
    initialState: {
      ordering: ORDERING_FIELD,
      filters: {
        page: 1,
        pageSize: COMMUNITIES_ADMIN_PAGE_SIZE,
        search: '',
        company: [],
        companyStatus: [],
        auditTypes: [],
      },
    },
  });

  const { isLoading, isFetching, refetch } = useQuery({
    queryKey: ['communities', filters, ordering],
    queryFn: () =>
      getAllCommunities({
        page: filters.page,
        page_size: filters.pageSize,
        ordering: ordering,
        search: filters?.search,
        search_include_competitors: true,
        companies: filters?.company,
        companies_relation: COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY,
        companies_status: filters?.companyStatus,
        audit_types: filters?.auditTypes,
      }),
    onSuccess: (data) => {
      setItems(data);
    },
    refetchOnWindowFocus: false,
  });

  return (
    <Container>
      <CommunitiesContext.Provider
        value={{
          loading: isLoading || isFetching,
          items: items ?? [],
          totalItemsCount,
          filters,
          manageContactsOpened,
          manageIncentivesOpened,
          setPage,
          setOrdering,
          setFilters,
          refetch,
          setSelectedCommunityInfo,
          setManageContactsOpened,
          setManageIncentivesOpened,
        }}
      >
        {manageContactsOpened && (
          <ManageContactsModal
            openModal={manageContactsOpened}
            communityInfo={selectedCommunityInfo}
            onClose={() => {
              setManageContactsOpened(false);
              setSelectedCommunityInfo(undefined);
            }}
          />
        )}
        {manageIncentivesOpened && (
          <ManageIncentivesModal
            openModal={manageIncentivesOpened}
            communityInfo={selectedCommunityInfo}
            onClose={() => {
              setManageIncentivesOpened(false);
              setSelectedCommunityInfo(undefined);
            }}
          />
        )}
        <CommunitiesHeader />
        <CommunitiesControls />
        <CommunitiesTable />
      </CommunitiesContext.Provider>
    </Container>
  );
};

export default Communities;
